import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ApplicationStep,
  ApplicationStepPayloads,
  ApplicationSubmitBody,
  ApplicationView,
} from '@tex/finance-shared'
import { ApiError, apiGet, apiSend } from './api'
import { caseKeys } from './cases'
import { borrowerKeys } from './participants'

const IDEMPOTENCY_HEADER = 'Idempotency-Key'

export const applicationKeys = {
  detail: (caseId: string) => ['cases', caseId, 'application'] as const,
}

/** The borrower's private draft; 403 for dealers, so only enable for borrower views. */
export function useApplication(caseId: string, enabled = true) {
  return useQuery({
    queryKey: applicationKeys.detail(caseId),
    enabled,
    queryFn: () => apiGet<ApplicationView>(`/api/v1/cases/${caseId}/application`),
  })
}

export interface SaveStepVars<S extends ApplicationStep = ApplicationStep> {
  step: S
  expected_version: number
  payload: ApplicationStepPayloads[S]
}

function useApplicationMutation<TVars>(caseId: string, fn: (vars: TVars) => Promise<ApplicationView>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (view) => {
      qc.setQueryData(applicationKeys.detail(caseId), view)
      // Stage moves (lead → application → review) show on the case itself and in the borrower's list.
      return Promise.all([qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }), qc.invalidateQueries({ queryKey: borrowerKeys.cases })])
    },
    // A conflict means our `version` is stale: refetch so the form can show the newer draft and resave.
    onError: (err) => {
      if (err instanceof ApiError && (err.code === 'version_conflict' || err.code === 'application_locked')) {
        return qc.invalidateQueries({ queryKey: applicationKeys.detail(caseId) })
      }
    },
  })
}

export function useSaveApplicationStep(caseId: string) {
  return useApplicationMutation(caseId, ({ step, ...body }: SaveStepVars) =>
    apiSend<ApplicationView>('PUT', `/api/v1/cases/${caseId}/application/steps/${step}`, body),
  )
}

/** `idempotencyKey` is minted once per confirmation so a retry replays the same submission. */
export function useSubmitApplication(caseId: string, idempotencyKey: string) {
  return useApplicationMutation(caseId, (body: ApplicationSubmitBody) =>
    apiSend<ApplicationView>('POST', `/api/v1/cases/${caseId}/application/submit`, body, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
  )
}
